import { request } from '../../Utils/request';

interface FileUploadRequest {
  folder: string;
  fileType: string;
}

interface SignedUrlResponse {
  folder: string;
  fileType: string;
  uploadUrl: string;
  fileKey: string;
  expiresIn: number;
}

interface GetSignedUrlsResponse {
  status: boolean;
  data: {
    files: SignedUrlResponse[];
  };
}



interface GetPublicUrlResponse {
  status: boolean;
  data: {
    files: [{
      viewUrl: string;
    }];
  };
}

/**
 * Gets signed URLs for file uploads
 * @param files Array of files containing folder and fileType information
 * @returns Promise containing signed URLs for each file
 */
export const getSignedUploadUrls = async (
  files: FileUploadRequest[]
): Promise<GetSignedUrlsResponse> => {
  try {
    return await request<GetSignedUrlsResponse, { files: FileUploadRequest[] }>(
      '/upload/signed-url',
      {
        method: 'POST',
        body: { files },
        authorization: true,
      }
    ).then(res => {
      console.log('signed url response', res);
      return res;
    });
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`${error.message}`);
    }
    throw new Error('Failed to get signed URLs: Unknown error occurred');
  }
};

/**
 * Uploads a file to the storage using a signed URL through our backend
 * @param file The file to upload
 * @param signedUrl The signed URL from getSignedUploadUrls
 * @returns Promise that resolves when upload is complete
 */
export const uploadFileToSignedUrl = async (
  file: File,
  signedUrl: string
): Promise<any> => {
  try {
    //older code for reference
    // headers: {
    //   'Content-Type': file.type,
    //   'Authorization': `Bearer ${localStorage.getItem('token')}`
    // },

    // Get token from Redux store to match the request utility
    const { store } = await import('../../store');
    const state = store.getState();
    const token = state.auth?.token;
    
    const headers: Record<string, string> = {
      'Content-Type': file.type,
    };
    
    // Only add Authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(signedUrl, {
      method: 'PUT',
      body: file,
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Upload failed:', {
        status: response.status,
        statusText: response.statusText,
        error: errorText
      });
      throw new Error(`Upload failed: ${response.statusText} - ${errorText}`);
    }
    
    console.log('upload response', response.status);
    return response
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Failed to upload file: ${error.message}`);
    }
    throw new Error('Failed to upload file: Unknown error occurred');
  }
};


/**
 * Fetches the public URL of an uploaded file using its fileKey
 * @param fileKey The fileKey returned after upload
 * @param folder The folder where the file was uploaded
 * @returns Public URL as a string
 */
export const getPublicUrlFromFileKey = async (
  fileKey: string,
  folder: string
): Promise<GetPublicUrlResponse> => {
  try {
    return await request<GetPublicUrlResponse, { folder: string, files: { fileKey: string }[] }>(
      '/upload/view',
        {
        method: 'POST',
        body: {
          folder: folder,
          files: [{ fileKey: fileKey }]
        },
        authorization: true,
      } 
    ).then(res => {
      console.log('public url response', res);
      return res;
    });
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Failed to get public URL: ${error.message}`);
    }
    throw new Error('Failed to get public URL: Unknown error occurred');
  }
};

/**
 * Toggles the favorite status of a place
 * @param placeId The ID of the place to toggle favorite status
 * @returns Promise<{ success: boolean }> - API response
 */
export const togglePlaceFavorite = async (placeId: string): Promise<{ success: boolean }> => {
  try {
    return await request<{ success: boolean }>(`/places/${placeId}/like`, {
      method: 'POST',
      authorization: true,
    });
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Failed to toggle favorite: ${error.message}`);
    }
    throw new Error('Failed to toggle favorite: Unknown error occurred');
  }
};

// Example usage:
// const files = [
//   { folder: 'profile', fileType: 'image/png' },
//   { folder: 'documents', fileType: 'application/pdf' }
// ];
// const signedUrls = await getSignedUploadUrls(files);
// const file = new File(['...'], 'image.png', { type: 'image/png' });
// await uploadFileToSignedUrl(file, signedUrls.data.files[0].uploadUrl);
